import {
  getUrlObject,
  getDatabrainSystemCode,
  getDatabrainSystemCodeFromPath,
  isCompleteHttpUrl,
  isDatabrainHostname,
} from './urlConfig';
import {
  getDefaultUrlCollectionAnalysisUrlInfoResult,
  getEmptyUrlCollectionEntityDetailsResult,
  getUrlCollectionBusinessPageNameByUrlObject,
} from './urlCollectionCommon';
import { Permission, Status, URL_COLLECTION_ANALYSIS_FAILURE_REASON } from '@home/enums/urlCollection';
import { DBSystemCode } from '@home/_config/config';

import type {
  IAnalysisUrlInfoParams,
  IAnalysisUrlInfoResult,
  IAnalysisUrlInfoHandler,
  IGetEntityDetailsByIdHandler,
  IGetEntityDetailsByIdResult,
} from '@home/enums/urlCollection';
import type {
  IRestoreDefaultUrlCollectionItemParams,
  IRestoreDefaultUrlCollectionItemResult,
} from '@home/types/urlCollection';

const getDefaultAnalysisUrlInfoResult = getDefaultUrlCollectionAnalysisUrlInfoResult;
const getDefaultEntityDetailsByIdResult = getEmptyUrlCollectionEntityDetailsResult;

interface IUrlCollectionSystemRegistryItem {
  analysisUrlInfoLoader?: () => Promise<Record<string, any>>;
  entityDetailsByIdLoader?: () => Promise<Record<string, any>>;
  defaultShortcutRestoreLoader?: () => Promise<Record<string, any>>;
}

interface IUrlCollectionDispatchMeta {
  rawUrl: string;
  pagePath: string;
  urlObj: URL | null;
  systemCode: string;
  failureReason?: IAnalysisUrlInfoResult['failure_reason'];
}

const getLoadedHandler = async <T>(
  loaderMap: Record<string, () => Promise<Record<string, any>>>,
  systemCode: string,
  handlerName: string,
): Promise<T | null> => {
  const loader = loaderMap[systemCode];

  if (!loader) {
    return null;
  }

  const handlerModule = await loader();
  const handler = handlerModule?.[handlerName];

  return typeof handler === 'function' ? (handler as T) : null;
};

const URL_COLLECTION_SYSTEM_REGISTRY: Record<string, IUrlCollectionSystemRegistryItem> = {
  [DBSystemCode.Home]: {
    analysisUrlInfoLoader: () => import('@home/_config/urlCollection'),
    entityDetailsByIdLoader: () => import('@home/_config/urlCollection'),
  },
  [DBSystemCode.StudioHub]: {
    analysisUrlInfoLoader: () => import('@home/utils/studioUrlCollection'),
    entityDetailsByIdLoader: () => import('@home/utils/studioUrlCollection'),
  },
  [DBSystemCode.Dashboard]: {
    analysisUrlInfoLoader: () => import('@/pages/dashboard/utils/urlCollection'),
    entityDetailsByIdLoader: () => import('@/pages/dashboard/utils/urlCollection'),
    defaultShortcutRestoreLoader: () => import('@/pages/dashboard/utils/urlCollection'),
  },
  [DBSystemCode.Benchmark]: {
    analysisUrlInfoLoader: () => import('@/pages/benchmark/utils/urlCollection'),
    entityDetailsByIdLoader: () => import('@/pages/benchmark/utils/urlCollection'),
  },
  [DBSystemCode.Intelligence]: {
    analysisUrlInfoLoader: () => import('@intelligence/v3/_config/utils'),
    entityDetailsByIdLoader: () => import('@intelligence/v3/_config/utils'),
    defaultShortcutRestoreLoader: () => import('@intelligence/v3/_config/utils'),
  },
  [DBSystemCode.Opinions]: {
    analysisUrlInfoLoader: () => import('@op/_config/utils'),
    entityDetailsByIdLoader: () => import('@op/_config/utils'),
    defaultShortcutRestoreLoader: () => import('@op/_config/utils'),
  },
  [DBSystemCode.Marketing]: {
    analysisUrlInfoLoader: () => import('@opm/_config/utils'),
    entityDetailsByIdLoader: () => import('@opm/_config/utils'),
  },
  [DBSystemCode.Globalpedia]: {
    analysisUrlInfoLoader: () => import('@gp/_config/urlCollection'),
    entityDetailsByIdLoader: () => import('@gp/_config/urlCollection'),
  },
  [DBSystemCode.Mgmt]: {
    analysisUrlInfoLoader: () => import('@sg/_config/urlCollection'),
    entityDetailsByIdLoader: () => import('@sg/_config/urlCollection'),
  },
  [DBSystemCode.UserCenter]: {
    analysisUrlInfoLoader: () => import('@user-center/_config/urlCollection'),
    entityDetailsByIdLoader: () => import('@user-center/_config/urlCollection'),
  },
  [DBSystemCode.Datalab]: {
    analysisUrlInfoLoader: () => import('@/pages/datalab/utils/urlCollection'),
    entityDetailsByIdLoader: () => import('@/pages/datalab/utils/urlCollection'),
  },
};

const getUrlCollectionAnalysisFailureReason = (
  result: IAnalysisUrlInfoResult,
): IAnalysisUrlInfoResult['failure_reason'] => {
  if (result.success) {
    return undefined;
  }

  if (result.failure_reason) {
    return result.failure_reason;
  }

  if (result.permission === Permission.NoPermission) {
    return URL_COLLECTION_ANALYSIS_FAILURE_REASON.NoPermission;
  }

  if (result.status === Status.Unavailable) {
    if (result.system) {
      return URL_COLLECTION_ANALYSIS_FAILURE_REASON.Unavailable;
    }

    return URL_COLLECTION_ANALYSIS_FAILURE_REASON.UnsupportedUrl;
  }

  return URL_COLLECTION_ANALYSIS_FAILURE_REASON.Unknown;
};

const normalizeAnalysisUrlInfoResult = (result: IAnalysisUrlInfoResult): IAnalysisUrlInfoResult => {
  return {
    ...result,
    failure_reason: getUrlCollectionAnalysisFailureReason(result),
  };
};

const mergeAnalysisUrlInfoResult = (
  fallbackResult: IAnalysisUrlInfoResult,
  result?: IAnalysisUrlInfoResult | null,
): IAnalysisUrlInfoResult => {
  if (!result) {
    return fallbackResult;
  }

  return {
    ...fallbackResult,
    ...result,
    page_url: result.page_url || fallbackResult.page_url,
    page_name: result.page_name || fallbackResult.page_name,
  };
};

const getUrlCollectionPagePath = (urlObj: URL | null): string => {
  if (!urlObj) {
    return '';
  }

  return `${urlObj.pathname}${urlObj.search}`;
};

/**
 * 统一解析收藏 URL 的原始输入，产出子系统只需要消费的 pathname + search。
 */
const getUrlCollectionDispatchMeta = (url = ''): IUrlCollectionDispatchMeta => {
  const rawUrl = url.trim();

  if (!rawUrl) {
    return {
      rawUrl,
      pagePath: '',
      urlObj: null,
      systemCode: '',
      failureReason: URL_COLLECTION_ANALYSIS_FAILURE_REASON.InvalidUrl,
    };
  }

  if (isCompleteHttpUrl(rawUrl)) {
    const rawUrlObj = getUrlObject(rawUrl);
    const pagePath = getUrlCollectionPagePath(rawUrlObj);
    const pagePathUrlObj = getUrlObject(pagePath);

    if (!rawUrlObj || !pagePathUrlObj) {
      return {
        rawUrl,
        pagePath,
        urlObj: pagePathUrlObj,
        systemCode: '',
        failureReason: URL_COLLECTION_ANALYSIS_FAILURE_REASON.InvalidUrl,
      };
    }

    if (!isDatabrainHostname(rawUrlObj.hostname)) {
      return {
        rawUrl,
        pagePath,
        urlObj: pagePathUrlObj,
        systemCode: '',
        failureReason: URL_COLLECTION_ANALYSIS_FAILURE_REASON.UnsupportedUrl,
      };
    }

    const systemCode = getDatabrainSystemCodeFromPath(pagePath);

    return {
      rawUrl,
      pagePath,
      urlObj: pagePathUrlObj,
      systemCode,
      failureReason: systemCode ? undefined : URL_COLLECTION_ANALYSIS_FAILURE_REASON.UnsupportedSystem,
    };
  }

  if (rawUrl.startsWith('//') || !rawUrl.startsWith('/') || !isDatabrainHostname(window.location.hostname)) {
    return {
      rawUrl,
      pagePath: '',
      urlObj: null,
      systemCode: '',
      failureReason: URL_COLLECTION_ANALYSIS_FAILURE_REASON.UnsupportedUrl,
    };
  }

  const urlObj = getUrlObject(rawUrl);
  const pagePath = getUrlCollectionPagePath(urlObj);
  const systemCode = getDatabrainSystemCodeFromPath(pagePath);
  let nextFailureReason: IAnalysisUrlInfoResult['failure_reason'] | undefined;

  if (!urlObj) {
    nextFailureReason = URL_COLLECTION_ANALYSIS_FAILURE_REASON.InvalidUrl;
  } else if (!systemCode) {
    nextFailureReason = URL_COLLECTION_ANALYSIS_FAILURE_REASON.UnsupportedSystem;
  }

  return {
    rawUrl,
    pagePath,
    urlObj,
    systemCode,
    failureReason: nextFailureReason,
  };
};

/**
 * 收藏 URL 统一解析入口。
 *
 * 保留用户输入的原始 URL 到 page_url，同时把可分发的 pathname + search 归一到
 * page_path，再根据子系统 code 分发给各子系统解析方法。
 */
export const analysisUrlInfo = async (params: IAnalysisUrlInfoParams): Promise<IAnalysisUrlInfoResult> => {
  const dispatchMeta = getUrlCollectionDispatchMeta(params?.url || '');
  const { rawUrl, pagePath, urlObj, systemCode, failureReason } = dispatchMeta;
  const defaultResult = normalizeAnalysisUrlInfoResult(
    mergeAnalysisUrlInfoResult(
      getDefaultAnalysisUrlInfoResult(rawUrl, urlObj, {
        failureReason: failureReason || URL_COLLECTION_ANALYSIS_FAILURE_REASON.UnsupportedUrl,
        system: systemCode,
        pagePath,
      }),
      {
        ...getDefaultAnalysisUrlInfoResult(rawUrl, urlObj, {
          system: systemCode,
          pagePath,
        }),
        page_url: rawUrl,
        page_name: getUrlCollectionBusinessPageNameByUrlObject(urlObj) || pagePath,
      },
    ),
  );
  const systemRegistry = URL_COLLECTION_SYSTEM_REGISTRY[systemCode];

  if (!systemCode || failureReason) {
    return defaultResult;
  }

  try {
    const analysisLoaderMap: Record<string, () => Promise<Record<string, any>>> = {};

    if (systemRegistry?.analysisUrlInfoLoader) {
      analysisLoaderMap[systemCode] = systemRegistry.analysisUrlInfoLoader;
    }

    const handler = await getLoadedHandler<IAnalysisUrlInfoHandler>(analysisLoaderMap, systemCode, 'analysisUrlInfo');

    if (!handler) {
      return defaultResult;
    }

    const result = await handler({
      ...params,
      url: rawUrl,
      page_path: pagePath,
    });

    return normalizeAnalysisUrlInfoResult(mergeAnalysisUrlInfoResult(defaultResult, result));
  } catch (error) {
    return normalizeAnalysisUrlInfoResult(
      mergeAnalysisUrlInfoResult(
        getDefaultAnalysisUrlInfoResult(rawUrl, urlObj, {
          failureReason: URL_COLLECTION_ANALYSIS_FAILURE_REASON.HandlerError,
          system: systemCode,
          pagePath,
        }),
        defaultResult,
      ),
    );
  }
};

/**
 * 收藏实体详情统一查询入口。
 *
 * 先根据页面 URL 识别子系统，再分发给各子系统的实体详情查询方法。
 */
export const getEntityDetailsById = async (params: IAnalysisUrlInfoResult): Promise<IGetEntityDetailsByIdResult> => {
  const systemCode =
    params?.system ||
    getDatabrainSystemCodeFromPath(params?.page_path || '') ||
    getDatabrainSystemCode(params?.page_url || '');
  const defaultResult = getDefaultEntityDetailsByIdResult();
  const systemRegistry = URL_COLLECTION_SYSTEM_REGISTRY[systemCode];

  if (!systemCode) {
    return defaultResult;
  }

  try {
    const entityDetailsLoaderMap: Record<string, () => Promise<Record<string, any>>> = {};

    if (systemRegistry?.entityDetailsByIdLoader) {
      entityDetailsLoaderMap[systemCode] = systemRegistry.entityDetailsByIdLoader;
    }

    const handler = await getLoadedHandler<IGetEntityDetailsByIdHandler>(
      entityDetailsLoaderMap,
      systemCode,
      'getEntityDetailsById',
    );

    if (!handler) {
      return defaultResult;
    }

    const result = await handler(params);

    return result || defaultResult;
  } catch (error) {
    return defaultResult;
  }
};

/**
 * 收藏默认数据还原统一入口。
 *
 * 默认收藏的 URL / name / data 还原规则由各子系统自己实现，这里只负责
 * 根据 system 分发调用。
 */
export const restoreDefaultShortcutItem = async (
  params: IRestoreDefaultUrlCollectionItemParams,
): Promise<IRestoreDefaultUrlCollectionItemResult> => {
  if (!params?.is_default) {
    return {};
  }

  const systemCode =
    params?.system || getDatabrainSystemCodeFromPath(params?.url || '') || getDatabrainSystemCode(params?.url || '');
  const defaultResult: IRestoreDefaultUrlCollectionItemResult = {};
  const systemRegistry = URL_COLLECTION_SYSTEM_REGISTRY[systemCode];

  if (!systemCode) {
    return defaultResult;
  }

  try {
    const defaultShortcutRestoreLoaderMap: Record<string, () => Promise<Record<string, any>>> = {};

    if (systemRegistry?.defaultShortcutRestoreLoader) {
      defaultShortcutRestoreLoaderMap[systemCode] = systemRegistry.defaultShortcutRestoreLoader;
    }

    const handler = await getLoadedHandler<
      (params: IRestoreDefaultUrlCollectionItemParams) => Promise<IRestoreDefaultUrlCollectionItemResult>
    >(defaultShortcutRestoreLoaderMap, systemCode, 'restoreDefaultShortcutItem');

    if (!handler) {
      return defaultResult;
    }

    const result = await handler(params);

    return result || defaultResult;
  } catch (error) {
    return defaultResult;
  }
};
